/**
 * 미디어 카드 — 광고 소재와 오가닉 게시물을 **한 모양**으로 편다.
 *
 * 카드 하나: 머리(누구 · 언제 · 채널) → 실물 → 광고면 CTA 줄 → 발(오가닉 반응 숫자 · 원문 열기).
 * 둘 다 펼 때는 시각 내림차순으로 합친다(`mergeByTime`) — 같은 시각이면 받은 순서.
 */

import type { AdCreative } from 'src/types/ads-meta/ads-meta-domain';
import type { VideoUrlRequestItem } from 'src/types/search-all/video-url';
import type { ProductRankingSearchItem } from 'src/types/product-rankings/product-rankings-domain';

import { fNumber } from 'src/utils/format/format-number';
import { formatPrice } from 'src/utils/format/format-money';
import { formatNumber } from 'src/utils/format/format-utils';
import { toInfluencerDocIdVariants } from 'src/utils/influencer/influencer-doc-id';

import { BOOKMARK_AD_SOURCE } from 'src/constants/bookmark-sources';
import { PRODUCT_SOURCE_LABELS } from 'src/constants/product-sources';
import { pickAdLabels, AD_CTA_LABELS } from 'src/constants/ad-labels';

import { VIDEO_URL_MAX_SEC, VIDEO_URL_SOURCES } from 'src/types/search-all/video-url';

export type MediaCardStat = { icon: 'views' | 'likes' | 'comments' | 'rating' | 'reviews'; value: string };

export type MediaCard = {
  key: string;
  kind: 'ad' | 'organic' | 'product';
  /** 누구 것 — 광고는 브랜드, 오가닉은 계정 */
  name: string;
  /** 광고 파트너십이면 함께한 계정 · 없으면 빈 문자열 */
  partner: string;
  /** 얼굴 — 없으면 빈 문자열(머리글자) · 오가닉은 인플루언서 데이터에서 붙인다(`applyAccountProfiles`) */
  avatar: string;
  /** 오가닉 계정 팔로워 — 인플루언서 데이터에 없으면 `null` · 광고는 `null` */
  followers: number | null;
  /** 채널 `source` 값 — 광고는 `meta` */
  channel: string;
  /** 정렬·합치기 기준 epoch ms — 광고는 집행 시작, 오가닉은 게시 시각. 모르면 `null` */
  time: number | null;
  /** 광고 집행 구간 한 줄 — 오가닉은 빈 문자열 */
  runLabel: string;
  /** 광고가 지금 집행 중인가 — 오가닉은 `false` */
  active: boolean;
  /** 광고 «노출 100 미만» — 확인 전·오가닉은 `false`(표시하지 않는다) */
  lowImpressions: boolean;
  thumb: string;
  video: boolean;
  /** 수집 때 받아 지금 재생할 수 있는 영상 주소 — 광고만(만료되면 빈 문자열) · 없으면 빈 문자열 */
  videoSrc: string;
  /**
   * 유효 주소를 물을 때 싣는 것 — 아니면 `null`.
   * 오가닉: 인스타그램·틱톡·유튜브 영상(2분 미만·길이 모름). 광고: 영상 광고 — `videoSrc` 가 비었거나 재생에 실패했을 때만 묻는다.
   */
  videoAsk: VideoUrlRequestItem | null;
  /** 여러 장이면 장 수 · 아니면 0 */
  slides: number;
  /** 광고 CTA 버튼 문구 · 없으면 빈 문자열 */
  cta: string;
  /** 광고 랜딩 도메인 · 없으면 빈 문자열 */
  landing: string;
  /** 광고 랜딩 주소 · 없으면 빈 문자열 */
  landingUrl: string;
  stats: MediaCardStat[];
  /** 원문 — 광고 라이브러리 · 게시물 */
  href: string;
  title: string;
  /** 같은 실물끼리 같은 값 — 광고는 이미지 클러스터(없으면 id), 오가닉은 글 id */
  visualKey: string;
  /** 오가닉만 — 북마크 부품·상세 드로워가 받는 글 원본 */
  post?: Record<string, unknown>;
  /** 광고만 — 상세 드로워가 받는 광고 원본 */
  ad?: AdCreative;
  /** 상품만 — 상세 다이얼로그가 받는 판매몰 상품 */
  product?: ProductRankingSearchItem;
};

const DAY_SECONDS = 24 * 60 * 60;

/** Meta CTA 버튼의 한국어 문구 — 모르는 코드면 광고주가 쓴 원문을 그대로 쓴다 */
const CTA_LABEL = pickAdLabels(AD_CTA_LABELS, 'full');

function text(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 시각 → epoch ms. 초 단위 숫자·ISO·`yyyyMMdd…` 문자열 · `yyyyMMdd` 숫자를 받는다. 못 읽으면 `null`
 * ⚠️ `postDate` 는 색인이 숫자(long) `20261020` 로 준다 — 초로 읽으면 1970년이 된다(지식인처럼 `timestamp` 가 없는 채널).
 */
export function toEpochMs(v: unknown): number | null {
  if (typeof v === 'number' && Number.isInteger(v) && v >= 19000101 && v <= 29991231) return toEpochMs(String(v));
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v < 1e12 ? v * 1000 : v;
  if (typeof v !== 'string' || !v) return null;
  if (/^\d+$/.test(v)) {
    if (v.length === 8) return new Date(+v.slice(0, 4), +v.slice(4, 6) - 1, +v.slice(6, 8)).getTime();
    return toEpochMs(Number(v));
  }
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

/**
 * 게시 시각 → 「방금 전 · N분 전 · N시간 전 · N일 전」, 30일이 넘으면 날짜.
 * @param now 기준 시각 — 테스트가 고정한다
 */
export function toRelativeLabel(time: number | null, now: number = Date.now()): string {
  if (time == null) return '';
  const minutes = Math.floor((now - time) / 60000);
  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}시간 전`;
  if (minutes < 60 * 24 * 30) return `${Math.floor(minutes / (60 * 24))}일 전`;
  return dotDate(time);
}

/** 게시물 `videosMeta[0].duration`(초) — 없거나 숫자가 아니면 `null` */
function readDurationSec(meta: unknown): number | null {
  const first = Array.isArray(meta) ? (meta[0] as Record<string, unknown> | undefined) : undefined;
  const n = Number(first?.duration);
  return first?.duration != null && Number.isFinite(n) && n > 0 ? n : null;
}

/** fbcdn 서명 주소의 만료 시각(`oe=` 16진 초) → epoch ms · 없으면 `null` */
export function readSignedUrlExpiry(url: string): number | null {
  const hex = /[?&]oe=([0-9a-fA-F]+)/.exec(url)?.[1];
  const sec = hex ? parseInt(hex, 16) : NaN;
  return Number.isFinite(sec) && sec > 0 ? sec * 1000 : null;
}

/** 이만큼(ms) 안에 만료되는 수집 주소는 만료된 것으로 본다 */
const SIGNED_URL_SKEW_MS = 60 * 1000;

/** `2026.09.14` — 로컬 날짜 */
function dotDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 격자의 「1/N」에 쓸 **칸 수** — 겹침을 걷고 빈 값을 버린 뒤 센다.
 * ⚠️ 한 장이면 **0 이다** — 배지를 달지 않는다(`card.slides > 1` 일 때만 그린다).
 */
function slideCount(urls: unknown[]): number {
  const kept = new Set(urls.filter((u): u is string => typeof u === 'string' && u.length > 0));
  return kept.size > 1 ? kept.size : 0;
}

/**
 * 광고 → 카드. 「광고 · 2026.09.14 - 진행 중 (N일)」
 * 수집 때 받은 영상 주소가 만료됐으면(`oe`) 싣지 않고 `videoAsk` 로 새 주소를 묻게 한다.
 * @param now 기준 시각 — 테스트가 고정한다
 */
export function toAdMediaCard(ad: AdCreative, now: number = Date.now()): MediaCard {
  const collected = ad.media.find((m) => m.type === 'video' && m.video)?.video ?? '';
  const expiry = collected ? readSignedUrlExpiry(collected) : null;
  const expired = expiry != null && expiry - now < SIGNED_URL_SKEW_MS;
  const libraryUrl = ad.adLibraryUrl || `https://www.facebook.com/ads/library?id=${ad.id}`;
  const start = toEpochMs(ad.startTs) ?? toEpochMs(ad.startDate);
  const end = toEpochMs(ad.endDate);
  const days = Math.max(0, Math.floor(ad.durationSeconds / DAY_SECONDS));
  const range = start == null ? '' : `${dotDate(start)} - ${ad.isActive || end == null ? '진행 중' : dotDate(end)}`;
  const partnership = ad.adDeliveryType === 'partnership';
  return {
    key: `ad:${ad.id}`,
    kind: 'ad',
    name: ad.brand || ad.pageName,
    partner: partnership ? ad.partnerPageName || (ad.publisherName !== ad.brand ? ad.publisherName : '') : '',
    avatar: ad.publisherPic,
    followers: null,
    channel: 'meta',
    time: start,
    runLabel: range ? `${range} (${fNumber(days)}일)` : '',
    active: ad.isActive,
    lowImpressions: ad.lowImpressions === true,
    thumb: ad.cover,
    video: ad.coverIsVideo,
    videoSrc: expired ? '' : collected,
    /**
     * 🚨 **끝난 광고는 새 주소를 묻지 않는다** — 집행이 끝나면 상류에 영상이 없어 묻는 값이 없다.
     *   묻는 쪽이 한 건씩 도는 바깥 호출이라, 안 나올 것을 부르면 목록을 펼 때마다 헛돈다.
     * ⚠️ 그래서 끝난 광고의 영상은 **섬네일까지**다(수집 때 받은 주소가 살아 있으면 그건 그대로 쓴다).
     */
    videoAsk: ad.isActive && collected && /^\d+$/.test(ad.id) ? { id: ad.id, source: 'meta', link: libraryUrl } : null,
    slides: ad.mediaCount > 1 ? ad.mediaCount : 0,
    cta: CTA_LABEL[ad.ctaType] ?? ad.ctaText,
    landing: ad.landingDomain,
    landingUrl: ad.landingUrl,
    stats: [],
    href: ad.adLibraryUrl,
    title: ad.title || ad.bodyText || ad.caption || ad.linkDescription,
    visualKey: `ad:${ad.imageClusterId || ad.id}`,
    ad,
  };
}

/**
 * 판매몰 상품 → 카드. 머리 둘째 줄 「올리브영 · 3위」, CTA 줄 자리에 판매가 · 할인율, 발에 별점 · 리뷰 수.
 * 채널 표시는 없다(`channel` 빈 문자열) — 판매몰 이름이 머리에 있다.
 */
export function toProductMediaCard(item: ProductRankingSearchItem): MediaCard {
  const mall = PRODUCT_SOURCE_LABELS[item.source] ?? item.source;
  const rank = item.rank != null ? `${fNumber(item.rank)}위` : '';
  return {
    key: `product:${item.source}:${item.postId}`,
    kind: 'product',
    name: item.brand || mall,
    partner: '',
    avatar: '',
    followers: null,
    channel: '',
    time: toEpochMs(item.date),
    runLabel: [mall, rank].filter(Boolean).join(' · '),
    active: false,
    lowImpressions: false,
    thumb: item.imageUrl || item.images[0] || '',
    video: false,
    videoSrc: '',
    videoAsk: null,
    /**
     * 몇 장인가 — 격자가 「1/N」을 달아 **더 있다는 것**을 알린다.
     * 🚨 0 으로 박아 두면 여러 장이어도 한 장처럼 보인다 — 상세를 열어야 캐러셀이 있는 줄 안다.
     * ⚠️ 세는 법이 상세의 칸과 **같아야 한다**(`toMediaSlides` — 대표 이미지 + `images`, 겹침 제거).
     *   상세는 여기에 상세페이지 이미지를 더 얹으므로 실제로는 이보다 많을 수 있다.
     */
    slides: slideCount([item.imageUrl, ...item.images]),
    cta: formatPrice(item.displayPrice, item.currency),
    landing: item.discountRate ? `${Math.round(item.discountRate)}%` : '',
    landingUrl: item.link,
    stats: [
      ...(item.starCount ? [{ icon: 'rating' as const, value: item.starCount.toFixed(1) }] : []),
      ...(item.commentCount ? [{ icon: 'reviews' as const, value: formatNumber(item.commentCount) }] : []),
    ],
    href: item.link,
    title: item.title,
    visualKey: `product:${item.source}:${item.postId}`,
    product: item,
  };
}

/** 오가닉(ingest 된 글) → 카드 */
export function toOrganicMediaCard(post: Record<string, unknown>, index: number): MediaCard {
  const source = text(post.source);
  const id = text(post.id) || text(post.link) || `organic-${index}`;
  const video = source === 'tiktok' || source === 'youtube' || /video|reel|short/i.test(text(post.contentType)) || post.isVideo === true;
  const askSource = VIDEO_URL_SOURCES.find((s) => s === source);
  const link = text(post.link);
  const duration = readDurationSec(post.videosMeta);
  // 길이가 2분 이상이면 서버를 부르지 않는다 — 길이가 비어 있으면 묻는다(서버가 첫 조회 때 잰다)
  const askable = video && askSource && text(post.id) && link && !(duration != null && duration >= VIDEO_URL_MAX_SEC);
  return {
    key: `organic:${id}`,
    kind: 'organic',
    name: text(post.account) || text(post.author),
    partner: '',
    avatar: '',
    followers: null,
    channel: source,
    time: toEpochMs(post.timestamp) ?? toEpochMs(post.postDate) ?? toEpochMs(post.date),
    runLabel: '',
    active: false,
    lowImpressions: false,
    thumb: text(post.imageS3Url) || text(post.imageUrl),
    videoSrc: '',
    videoAsk: askable && askSource
      ? { id: text(post.id), source: askSource, link, ...(duration != null ? { durationSec: duration } : {}) }
      : null,
    video,
    /* ⚠️ 글은 여기서 안 센다 — 이미지 목록을 상세가 `pickPostImages` 로 따로 짓는다(칸 규칙이 다르다) */
    slides: 0,
    cta: '',
    landing: '',
    landingUrl: '',
    stats: [
      { icon: 'views', value: formatNumber(num(post.viewCount)) },
      { icon: 'likes', value: formatNumber(num(post.likeCount)) },
      { icon: 'comments', value: formatNumber(num(post.commentCount)) },
    ],
    href: text(post.link),
    title: text(post.title) || text(post.content),
    visualKey: `organic:${id}`,
    post,
  };
}

/**
 * 그리드에 펼 카드만 — 실물(썸네일)이 없는 카드는 빼되 글이 있는 오가닉은 글자 카드로 남기고, 같은 카드·같은 실물(광고 변형)은 처음 하나만.
 * 페이지가 겹쳐 오거나 같은 소재를 여러 번 집행해도 그리드가 같은 그림을 늘어놓지 않는다.
 */
export function dedupeCards(cards: MediaCard[]): MediaCard[] {
  const seen = new Set<string>();
  return cards.filter((c) => {
    const textOnly = !c.thumb && c.kind === 'organic' && Boolean(c.title);
    if ((!c.thumb && !textOnly) || seen.has(c.key) || seen.has(c.visualKey)) return false;
    seen.add(c.key);
    seen.add(c.visualKey);
    return true;
  });
}

/** 두 목록을 시각 내림차순으로 합친다 — 시각을 모르는 카드는 0 으로 보고, 같으면 앞 목록이 먼저 */
export function mergeByTime(a: MediaCard[], b: MediaCard[]): MediaCard[] {
  const out: MediaCard[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if ((a[i].time ?? 0) >= (b[j].time ?? 0)) {
      out.push(a[i]);
      i += 1;
    } else {
      out.push(b[j]);
      j += 1;
    }
  }
  return [...out, ...a.slice(i), ...b.slice(j)];
}

/**
 * 합친 목록에서 보여도 되는 시각의 하한 — 더 받을 것이 남은 갈래의 마지막 카드 시각 중 큰 값.
 * 더 받을 것이 없는 갈래는 하한을 만들지 않는다. 하한이 없으면 `null`(전부 보인다).
 */
export function releaseCutoff(lanes: { cards: MediaCard[]; hasMore: boolean }[]): number | null {
  const tails = lanes
    .filter((l) => l.hasMore && l.cards.length > 0)
    .map((l) => l.cards[l.cards.length - 1].time ?? 0);
  return tails.length > 0 ? Math.max(...tails) : null;
}

/** 인플루언서 데이터 채널 — 문서 id 는 `{채널}_{계정}` 이다 */
export const PROFILE_CHANNELS = ['instagram', 'tiktok', 'youtube', 'x', 'threads'];

/**
 * ⚠️ `mirrored` 는 **얼굴이 자사 미러인가**다 — 원본 주소는 서명이 만료되면 403 이라
 *   「주소가 있다」와 「불러진다」가 다르다. 대안 얼굴이 있는 쪽은 이걸 보고 차례를 정한다.
 */
export type AccountProfile = { avatar: string; followers: number | null; mirrored: boolean };

/** 계정 찾기 열쇠 — 채널 + 소문자 계정 */
export function profileKey(channel: string, account: string): string {
  return `${channel}_${account.toLowerCase()}`;
}

/**
 * 글 더미 → **계정 한 벌**(채널 + 아이디). 실시간 조회 창구가 이 짝으로 묻는다.
 * ⚠️ `toProfileDocIds` 와 **같은 계정을 고른다** — 한쪽만 넓히면 색인에서 못 찾은 것을 창구에도 못 묻는다.
 */
export function toProfileAccounts(posts: Record<string, unknown>[]): { channel: string; account: string }[] {
  const seen = new Set<string>();
  const out: { channel: string; account: string }[] = [];
  posts.forEach((p) => {
    const channel = text(p.source);
    const account = text(p.account) || text(p.author);
    if (!account || !PROFILE_CHANNELS.includes(channel)) return;
    const key = profileKey(channel, account);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ channel, account });
  });
  return out;
}

/**
 * 오가닉 글(ingest 됨) → 인플루언서 문서 id 목록(`docs.byIds`). 표기 차이는 `toInfluencerDocIdVariants` 가 덮는다.
 * 계정은 카드 이름과 같은 규칙(`account` → `author`)으로 읽는다.
 * @returns 겹치지 않는 id · 정렬됨(캐시 서명이 흔들리지 않게)
 */
export function toProfileDocIds(posts: Record<string, unknown>[]): string[] {
  const ids = new Set<string>();
  posts.forEach((p) => {
    const channel = text(p.source);
    const account = text(p.account) || text(p.author);
    if (!account || !PROFILE_CHANNELS.includes(channel)) return;
    toInfluencerDocIdVariants(channel, account).forEach((id) => ids.add(id));
  });
  return [...ids].sort();
}

/**
 * 인플루언서 문서 → 계정 열쇠별 얼굴·팔로워. 얼굴은 자사 미러(`profilePicS3Url`) 먼저, 없으면 원본(`profilePicUrl`).
 */
export function readAccountProfiles(rows: unknown[]): Map<string, AccountProfile> {
  const out = new Map<string, AccountProfile>();
  rows.forEach((raw) => {
    const doc = (raw != null && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const id = text(doc.id);
    const channel = text(doc.channel) || id.split('_')[0];
    const account = text(doc.account) || id.slice(channel.length + 1);
    if (!channel || !account) return;
    const profile = (doc.profile != null && typeof doc.profile === 'object' ? doc.profile : {}) as Record<string, unknown>;
    const followers = Number(doc.followerCount);
    const mirror = text(profile.profilePicS3Url);
    out.set(profileKey(channel, account), {
      avatar: mirror || text(profile.profilePicUrl),
      followers: Number.isFinite(followers) && followers > 0 ? followers : null,
      mirrored: Boolean(mirror),
    });
  });
  return out;
}

/**
 * 카드 하나를 **북마크에 담을 모양**으로 — `{ id, source }` 한 쌍이다.
 *
 * 🔑 북마크는 문서에 `postId` 하나만 담고 **`source` 로 어느 원장에서 꺼낼지**를 가른다.
 *   글은 채널 이름, 광고는 `meta`, 상품은 **판매몰 이름**이다(같은 상품 id 가 몰마다 따로 있다).
 * ⚠️ 셋 중 어느 것도 아니면 `null` — 부르는 쪽이 담기 단추를 아예 그리지 않는다.
 */
export function toBookmarkTarget(card: MediaCard): { id: string; source: string } | null {
  if (card.post) {
    const id = text(card.post.id);
    return id ? { id, source: text(card.post.source) } : null;
  }
  if (card.ad?.id) return { id: String(card.ad.id), source: BOOKMARK_AD_SOURCE };
  if (card.product?.postId) return { id: String(card.product.postId), source: String(card.product.source ?? '') };
  return null;
}

/**
 * 계정 하나의 얼굴·팔로워 — 채널+아이디로 찾는다(대소문자 무시).
 * @returns 인플루언서 데이터에 없는 계정이면 `undefined`
 */
export function findAccountProfile(
  profiles: Map<string, AccountProfile>,
  channel: string,
  account: string,
): AccountProfile | undefined {
  return profiles.get(profileKey(channel, account));
}

/** 오가닉 카드에 계정 얼굴·팔로워를 붙인다 — 찾지 못한 카드는 그대로 */
export function applyAccountProfiles(cards: MediaCard[], profiles: Map<string, AccountProfile>): MediaCard[] {
  if (profiles.size === 0) return cards;
  return cards.map((c) => {
    if (c.kind !== 'organic') return c;
    const p = profiles.get(profileKey(c.channel, c.name));
    return p ? { ...c, avatar: p.avatar || c.avatar, followers: p.followers } : c;
  });
}

export type InterleaveLane = { cards: MediaCard[]; more: boolean; weight: number };

/**
 * 여러 원장을 비율대로 번갈아 끼운다 — 원장마다 기준(집행 기간 · 성과 점수 · 판매몰 순위 · 시각)이 달라 한 줄로 합칠 수 없을 때.
 * 한 바퀴에 원장마다 `weight` 칸씩 차례로 편다. 차례가 온 원장이 비었는데 더 받을 것이 남았으면 **거기서 멈춘다** —
 * 다음 페이지가 와도 이미 보인 순서가 바뀌지 않는다. 다 받은 원장은 건너뛴다.
 */
export function interleaveLanes(lanes: InterleaveLane[]): MediaCard[] {
  const out: MediaCard[] = [];
  const next = lanes.map(() => 0);
  for (;;) {
    let progressed = false;
    for (let k = 0; k < lanes.length; k += 1) {
      const lane = lanes[k];
      for (let w = 0; w < lane.weight; w += 1) {
        if (next[k] < lane.cards.length) {
          out.push(lane.cards[next[k]]);
          next[k] += 1;
          progressed = true;
        } else if (lane.more) {
          return out;
        } else {
          break;
        }
      }
    }
    if (!progressed) return out;
  }
}
